import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './RowActions.scss';
import { anchorFor, anchorStyle, type MenuAnchor } from './menu-anchor';

/**
 * Row action definition for the new table widget
 */
export interface RowAction<TRow = unknown> {
  /** Display name of the action */
  name: string;

  /** Icon to display (optional) */
  icon?: string;

  /** Function to execute when action is triggered */
  handler: (row: TRow, rowIndex: number) => void | Promise<void>;

  /** Whether action is disabled for this row */
  disabled?: (row: TRow) => boolean;

  /** Custom CSS class for the action */
  className?: string;

  /**
   * A submenu: this entry opens these instead of running its own handler. One
   * level deep, which is all the page DSL's borrowed action groups need — a
   * schedule row's "Job" entry holding the whole Jobs-page menu
   * (FEATURE-20260918-unified-job-menu-on-jobs-schedules-monitors).
   */
  children?: RowAction<TRow>[];
}

/**
 * Row actions menu component
 * Shows a menu button that opens a dropdown with available actions
 */
interface RowActionsMenuProps<TRow = unknown> {
  actions: RowAction<TRow>[];
  row: TRow;
  rowIndex: number;
}

export function RowActionsMenu<TRow = unknown>({ actions, row, rowIndex }: RowActionsMenuProps<TRow>) {
  const [isOpen, setIsOpen] = useState(false);
  // Which submenu is open, by index, and where to draw it. Only one at a time,
  // and closing the whole menu closes it.
  //
  // The submenu is position:FIXED, anchored to its parent entry's rect. It has
  // to escape the table: this menu lives inside the grid body, which clips, and
  // a flyout drawn inside that box was cut off — the pointer then landed on the
  // clipping container rather than on a descendant, which fired the dropdown's
  // mouseleave and shut the submenu under the cursor (job-access e2e, the
  // Schedules grid, FEATURE-20260918-unified-job-menu-on-jobs-schedules-monitors).
  const [openSub, setOpenSub] = useState<number | null>(null);
  const [subAnchor, setSubAnchor] = useState<MenuAnchor | null>(null);
  // Where to draw the dropdown, in viewport coordinates, captured when it opens.
  const [anchor, setAnchor] = useState<MenuAnchor | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const openSubmenuAt = (idx: number, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    // right-pinned to the parent entry's left edge; top-aligned with it, or
    // bottom-aligned when the entry sits in the lower half of the viewport
    setSubAnchor(anchorFor(r, Math.max(0, window.innerWidth - r.left), 0));
    setOpenSub(idx);
  };
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      // The menu is PORTALLED to the body, so it is not inside menuRef any more;
      // both the trigger and the menu itself count as "inside".
      const inTrigger = menuRef.current?.contains(t) ?? false;
      const inMenu = dropdownRef.current?.contains(t) ?? false;
      if (!inTrigger && !inMenu) setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setOpenSub(null);
      setAnchor(null);
    }
  }, [isOpen]);

  // Fixed coordinates go stale the moment anything scrolls or resizes under the
  // menu, and a menu floating away from its row is worse than no menu: close it.
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  const handleActionClick = async (action: RowAction<TRow>) => {
    setIsOpen(false);
    setOpenSub(null);
    try {
      await action.handler(row, rowIndex);
    } catch (error) {
      console.error(`Error executing action "${action.name}":`, error);
    }
  };

  return (
    <div className="row-actions-menu" ref={menuRef}>
      <button
        className="row-actions-button"
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            setIsOpen(false);
            return;
          }
          const r = e.currentTarget.getBoundingClientRect();
          setAnchor(anchorFor(r, Math.max(0, window.innerWidth - r.right), 4));
          setIsOpen(true);
        }}
        title="Row actions"
      >
        ⋮
      </button>

      {isOpen && anchor && createPortal((
        <div
          className="row-actions-dropdown"
          ref={dropdownRef}
          style={anchorStyle(anchor)}
          onMouseLeave={() => setOpenSub(null)}
        >
          {actions.map((action, idx) => {
            const isDisabled = action.disabled ? action.disabled(row) : false;
            const hasChildren = Boolean(action.children?.length);

            return (
              <div key={idx} className={hasChildren ? 'row-action-group' : undefined}>
                <button
                  className={`row-action-item ${action.className || ''} ${isDisabled ? 'disabled' : ''}`}
                  // Hover opens a group and closes any other — a submenu that
                  // needs a click to appear is a click nobody should have to
                  // make (glen, 2026-09-18). The click below still toggles it,
                  // for touch and for the keyboard.
                  onMouseEnter={(e) => {
                    if (hasChildren && !isDisabled) openSubmenuAt(idx, e.currentTarget);
                    else setOpenSub(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    // A group OPENS its submenu; anything else runs. Opening is
                    // idempotent rather than a toggle: hover already opened it
                    // by the time a click lands (a real pointer hovers on its
                    // way in, and so does Playwright), so a toggle here shut
                    // the submenu under whoever clicked the entry.
                    if (hasChildren) openSubmenuAt(idx, e.currentTarget);
                    else handleActionClick(action);
                  }}
                  disabled={isDisabled}
                >
                  {action.icon && <span className="action-icon">{action.icon}</span>}
                  <span className="action-name">{action.name}</span>
                  {hasChildren && <span className="row-action-caret">▸</span>}
                </button>

                {hasChildren && openSub === idx && subAnchor && (
                  <div
                    className="row-actions-submenu"
                    style={anchorStyle(subAnchor)}
                  >
                    {action.children!.map((child, cidx) => {
                      const childDisabled = child.disabled ? child.disabled(row) : false;
                      return (
                        <button
                          key={cidx}
                          className={`row-action-item ${child.className || ''} ${childDisabled ? 'disabled' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!childDisabled) handleActionClick(child);
                          }}
                          disabled={childDisabled}
                        >
                          <span className="action-name">{child.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ), document.body)}
    </div>
  );
}
